
import {
  defineComponent,
  toRefs,
  reactive,
  computed,
  ref,
  watch
} from "vue-demi";
import AdmTypography from "@afe/admirals-dev/src/components/AdmTypography";
import AdmSelect from "@afe/admirals-dev/src/components/AdmSelect";
import AdmTextField from "@afe/admirals-dev/src/components/AdmTextField";
import AdmSymbol from "@afe/admirals-dev/src/components/AdmSymbol";
import AdmPhone from "@afe/admirals-dev/src/components/AdmPhone";
import AdmButton from "@afe/admirals-dev/src/components/AdmButton";
import { useVuelidate } from "@vuelidate/core";
import {
  required,
  email,
  minLength,
  maxLength,
  helpers
} from "@vuelidate/validators";
import get from "lodash/get";
import { parsePhoneNumber } from "awesome-phonenumber";
import { registrationUnified } from "../DemoUniversalForm/api";
import CheckboxField from "./CheckboxField.vue";
import getCaptchaToken from "@/forms/captcha";
import { latinValidator } from "@/forms/service";
import { sameAs } from "vuelidate/lib/validators";

const phoneValidator = (value: string) => {
  const { valid } = parsePhoneNumber(value);

  return valid;
};

export default defineComponent({
  name: "marketing-registration-form",
  components: {
    AdmTypography,
    AdmSelect,
    AdmTextField,
    AdmSymbol,
    AdmPhone,
    AdmButton,
    CheckboxField
  },
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const { config } = toRefs(props);
    const passwordType = ref(true);
    const showPasswordHint = ref(false);
    const apiErrors = ref({});
    const loading = ref(false);
    const captcha = ref(null);
    const passwordFocus = ref(false);
    const passwordConfirmationFocus = ref(false);

    const state = reactive({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
      phone_number: "",
      marketingEmailsEnabled: false,
      country_id: config.value.selectedCountry
    });

    const rules = {
      first_name: {
        required: helpers.withMessage(
          config.value.fields.first_name.errors.required,
          required
        ),
        minLength: helpers.withMessage(
          config.value.fields.first_name.errors.minLength,
          minLength(1)
        ),
        maxLength: helpers.withMessage(
          config.value.fields.first_name.errors.maxLength,
          maxLength(64)
        ),
        noMatch: helpers.withMessage(
          config.value.fields.first_name.errors.noMatch,
          latinValidator
        )
      },
      last_name: {
        required: helpers.withMessage(
          config.value.fields.last_name.errors.required,
          required
        ),
        minLength: helpers.withMessage(
          config.value.fields.last_name.errors.minLength,
          minLength(1)
        ),
        maxLength: helpers.withMessage(
          config.value.fields.last_name.errors.maxLength,
          maxLength(64)
        ),
        noMatch: helpers.withMessage(
          config.value.fields.last_name.errors.noMatch,
          latinValidator
        )
      },
      email: {
        required: helpers.withMessage(
          config.value.fields.email.errors.required,
          required
        ),
        email: helpers.withMessage(
          config.value.fields.email.errors.noMatch,
          email
        ),
        minLength: helpers.withMessage(
          config.value.fields.email.errors.minLength,
          minLength(4)
        ),
        maxLength: helpers.withMessage(
          config.value.fields.email.errors.minLength,
          maxLength(255)
        )
      },
      password: {
        required: helpers.withMessage(
          config.value.fields.password.errors.required,
          required
        )
      },
      password_confirmation: {
        required: helpers.withMessage(
          config.value.fields.password_confirmation.errors.required,
          required
        ),
        notSame: helpers.withMessage(
          config.value.fields.password_confirmation.errors.notSame,
          sameAs("password")
        )
      },
      country_id: { required },
      phone_number: {
        required: helpers.withMessage(
          config.value.fields.phone_number.errors.noMatch,
          phoneValidator
        )
      }
    };

    const v$ = useVuelidate(rules, state);

    const togglePasswordType = () => {
      passwordType.value = !passwordType.value;
    };

    const phoneNumberError = computed(() => {
      if (get(apiErrors.value, "phone_number[0]") === "invalid") {
        return config.value.fields.phone_number.errors.noMatch;
      }

      if (get(apiErrors.value, "phone_number[0]") === "phone_number_used") {
        return config.value.fields.phone_number.errors.alreadyExists;
      }

      return get(v$.value, "phone_number.$errors[0].$message");
    });

    const emailError = computed(() => {
      return get(v$.value, "email.$errors[0].$message");
    });

    const firstNameError = computed(() => {
      return get(v$.value, "first_name.$errors[0].$message");
    });

    const lastNameError = computed(() => {
      return get(v$.value, "last_name.$errors[0].$message");
    });

    const passwordConfirmationError = computed(() => {
      return get(v$.value, "password_confirmation.$errors[0].$message");
    });

    const passwordError = computed(() => {
      return get(v$.value, "password.$errors[0].$message");
    });

    const passwordHints = computed(() => {
      const { hints } = config.value.fields.password;
      const rules: Record<any, string> = {
        uppercase: "[A-Z]",
        lowercase: "[a-z]",
        number: "[0-9]",
        specialChar: "[\x20-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]",
        minLength: "^.{8,128}$",
        latinOnly: "^[\x00-\x7F]+$"
      };

      return Object.keys(hints)
        .map(key => {
          if (!rules[key]) {
            return null;
          }
          return {
            label: hints[key],
            valid: new RegExp(rules[key]).test(state.password)
          };
        })
        .filter(Boolean);
    });

    const passwordCorrect = computed(() => {
      return passwordHints.value.findIndex(hint => hint && !hint.valid) === -1;
    });

    const formError = computed(() => {
      if (
        ["email_not_verified", "email_used"].includes(
          get(apiErrors.value, "entity[0]")
        )
      ) {
        return config.value.errors.email_used;
      }

      if (get(apiErrors.value, "entity[0]") === "email_disposable") {
        return config.value.errors.email_disposable;
      }

      if (get(apiErrors.value, "country_id[0]") === "country_not_configured") {
        return config.value.errors.general;
      }

      if (get(apiErrors.value, "general")) {
        return config.value.errors.general;
      }

      return;
    });

    watch(passwordCorrect, () => {
      showPasswordHint.value = !passwordCorrect.value;
    });

    const onSubmit = async () => {
      v$.value.$touch();

      showPasswordHint.value = !passwordCorrect.value;

      if (!passwordCorrect.value || v$.value.$invalid) {
        return;
      }

      loading.value = true;
      apiErrors.value = {};

      try {
        const captchaToken = await getCaptchaToken(
          {
            entity: state.email,
            company_id: config.value.params.company_id,
            country_id: state.country_id
          },
          captcha.value!
        );

        const { data } = await registrationUnified({
          apiUrl: config.value.apiUrl,
          _lang: config.value.lang,
          entity: state.email,
          first_name: state.first_name,
          last_name: state.last_name,
          password: state.password,
          phone_number: state.phone_number,
          country_id: state.country_id,
          captcha_token: captchaToken,
          marketing_emails_enabled: state.marketingEmailsEnabled,
          ...config.value.params
        });
        emit("success", { ...data, email: state.email });
      } catch ({ response }) {
        apiErrors.value = get(response, "data.fields_codes", { general: true });
      }
      loading.value = false;
    };

    return {
      onSubmit,
      config,
      loading,
      captcha,
      state,
      phoneNumberError,
      firstNameError,
      lastNameError,
      emailError,
      passwordConfirmationError,
      passwordFocus,
      passwordConfirmationFocus,
      passwordError,
      passwordType,
      togglePasswordType,
      passwordCorrect,
      passwordHints,
      showPasswordHint,
      formError,
      v$
    };
  }
});
